import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';

const RESET_LABEL_TIMER = 3000;

type UseSocialIconWithSuccessStateArgs = {
	action: () => Promise<boolean>;
	label: TranslationKey;
	successLabel?: TranslationKey;
};

type UseSocialIconWithSuccessStateResult = {
	data: {
		currentLabel: TranslationKey;
		successState: boolean | undefined;
	};
	actions: {
		onClick: () => Promise<void>;
	};
};

export function useSocialIconWithSuccessState({
	action,
	label,
	successLabel,
}: UseSocialIconWithSuccessStateArgs): UseSocialIconWithSuccessStateResult {
	const [successState, setSuccessState] = useState<boolean | undefined>(undefined);

	const onClick = useCallback(async () => {
		const success = await action();
		setSuccessState(success);
	}, [action]);

	useEffect(() => {
		if (typeof successState === 'undefined') return undefined;
		const timeout = window.setTimeout(() => setSuccessState(undefined), RESET_LABEL_TIMER);
		return () => window.clearTimeout(timeout);
	}, [successState]);

	const currentLabel = useMemo(() => {
		if (successState === true && successLabel) return successLabel;
		return label;
	}, [successState, successLabel, label]);

	return {
		data: { currentLabel, successState },
		actions: { onClick },
	};
}
